// src/pages/approval/ApprovalInbox.jsx
import React, { useEffect, useState, useCallback } from "react";
import api from "../../api";
import { useLocation } from "react-router-dom";
import ExpenseResolutionPreview, { buildExpenseResolutionProps } from "../../components/preview/expense/ExpenseResolutionPreview";
import IncomeResolutionPreview, { buildIncomeResolutionProps } from "../../components/preview/income/IncomeResolutionPreview";
import { RESOLUTION_DOCUMENT_TYPES } from "../../utils/resolutionDraft";
import LeaveRequestPreview, { buildLeaveRequestProps } from "../../components/preview/leave/LeaveRequestPreview";

import styles from "./ApprovalInbox.module.css";

const DOC_TYPE_LABEL = {
  income_draft: "수입기안",
  expense_draft: "지출기안",
  income_resolution: "수입결의",
  expense_resolution: "지출결의",
  leave_request: "휴가신청서",
};

const STATUS_LABEL = {
  draft: "작성중",
  pending: "결재중",
  approved: "승인",
  rejected: "반려",
};

const STATUS_COLOR = {
  draft: "#888",
  pending: "#e67e22",
  approved: "#27ae60",
  rejected: "#e74c3c",
};

// skipped(생략) = 결재 단계를 승인 없이 종결한 것(예: 퇴임). 회색 — 승인(초록)과 섞이지 않게.
const RESULT_LABEL = { pending: "대기", approved: "승인", rejected: "반려", skipped: "생략" };
const RESULT_COLOR = { pending: "#aaa", approved: "#27ae60", rejected: "#e74c3c", skipped: "#7f8c8d" };

export default function ApprovalInbox() {

  const location = useLocation();
  const [tab, setTab] = useState(() => {
    if (location.pathname === "/approval/done")       return "submitted";
    if (location.pathname === "/approval/inprogress") return "submitted";
    return "inbox";
  });


  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // 상세 보기 중인 item
  const [comment, setComment] = useState("");
  const [processing, setProcessing] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // ✅ 지출결의/수입결의 — WEHAGO 미리보기를 그리려면 inbox/my-submitted가 주는
  // 요약 데이터(template_data 없음)로는 부족해서 선택 시 상세를 한 번 더 조회한다.
  const [resolutionDetail, setResolutionDetail] = useState(null);
  useEffect(() => {
    const isLeave = selected?.document_type === "leave_request";
    if (!selected || (!isLeave && !RESOLUTION_DOCUMENT_TYPES.includes(selected.document_type))) {
      setResolutionDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // 휴가신청서는 전용 상세(승인(일정 취소됨) 상태 포함)
        const url = isLeave
          ? `/draft/leave-requests/${selected.document_id}/`
          : `/draft/documents/${selected.document_id}/`;
        const { data } = await api.get(url);
        if (!cancelled) setResolutionDetail(data);
      } catch (err) {
        console.error("❌ 지출결의 상세 조회 실패:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        tab === "inbox"
          ? "/draft/documents/inbox/"


          : "/draft/documents/my-submitted/";


      const { data } = await api.get(endpoint);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ 데이터 불러오기 실패:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    fetchData();
    setSelected(null);
    setComment("");
  }, [fetchData]);

  const handleApprove = async () => {
    if (!selected) return;
    if (!window.confirm("승인하시겠습니까?")) return;
    setProcessing(true);
    try {
      await api.post(`/draft/approval-lines/${selected.approval_line_id}/approve/`, {
        comment: comment || "",
      });
      alert("✅ 승인 완료되었습니다.");
      setSelected(null);
      setComment("");
      fetchData();
    } catch (err) {
      alert("오류: " + (err.response?.data?.error || "승인 실패"));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!comment.trim()) {
      alert("반려 사유를 입력해주세요.");
      return;
    }
    setProcessing(true);
    try {
      await api.post(`/draft/approval-lines/${selected.approval_line_id}/reject/`, {
        comment,
      });
      alert("반려 처리되었습니다.");
      setSelected(null);
      setComment("");
      setShowRejectModal(false);
      fetchData();
    } catch (err) {
      alert("오류: " + (err.response?.data?.error || "반려 실패"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className={styles.wrap}>
      {/* 탭 */}
      <div className={styles.tabBar}>
        <button
          className={tab === "inbox" ? styles.tabActive : styles.tab}
          onClick={() => setTab("inbox")}
        >
          📥 수신결재
          {tab !== "inbox" && items.length > 0 && (
            <span className={styles.badge}>{items.length}</span>
          )}
        </button>
        <button
          className={tab === "submitted" ? styles.tabActive : styles.tab}
          onClick={() => setTab("submitted")}
        >
          📤 상신한 문서
        </button>
      </div>

      <div className={styles.content}>
        {/* 목록 패널 */}
        <div className={styles.listPane}>
          <div className={styles.listHeader}>
            <span>{tab === "inbox" ? "내 결재 대기 목록" : "내가 상신한 문서"}</span>
            <button className={styles.refreshBtn} onClick={fetchData}>🔄</button>
          </div>

          {loading ? (
            <div className={styles.empty}>불러오는 중...</div>
          ) : items.length === 0 ? (
            <div className={styles.empty}>
              {tab === "inbox" ? "대기 중인 결재가 없습니다." : "상신한 문서가 없습니다."}
            </div>
          ) : (
            <ul className={styles.list}>
              {items.map((item) => {
                const docId = item.document_id;
                const isSelected = selected?.document_id === docId && selected?.approval_line_id === item.approval_line_id;
                return (
                  <li
                    key={`${docId}-${item.approval_line_id || ""}`}
                    className={`${styles.listItem} ${isSelected ? styles.listItemActive : ""}`}
                    onClick={() => { setSelected(item); setComment(""); setShowRejectModal(false); }}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.docType}>
                        {DOC_TYPE_LABEL[item.document_type] || item.document_type}
                      </span>
                      <span
                        className={styles.statusBadge}
                        style={{ color: STATUS_COLOR[item.status] }}
                      >
                        {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </div>
                    <div className={styles.itemTitle}>{item.title}</div>
                    <div className={styles.itemMeta}>
                      <span>{item.category}</span>
                      <span>·</span>
                      <span>{item.partner_name}</span>
                      <span>·</span>
                      <span>{Number(item.amount).toLocaleString()}원</span>
                    </div>
                    <div className={styles.itemDate}>
                      {tab === "inbox"
                        ? `결재구분: ${item.approval_type} (${item.order}번째)`
                        : `기안자: ${item.created_by_name}`}
                      <span style={{ marginLeft: 8, color: "#aaa" }}>{item.draft_date}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 상세 패널 */}
        <div className={styles.detailPane}>
          {!selected ? (
            <div className={styles.noSelect}>목록에서 문서를 선택하세요.</div>
          ) : (
            <>
              {/* 문서 정보 — 지출결의/수입결의는 WEHAGO 양식으로, 그 외는 기존 요약 표 그대로 */}
              {selected.document_type === "expense_resolution" ? (
                resolutionDetail ? (
                  <ExpenseResolutionPreview {...buildExpenseResolutionProps(resolutionDetail)} />
                ) : (
                  <div className={styles.detailCard}>불러오는 중...</div>
                )
              ) : selected.document_type === "leave_request" ? (
                resolutionDetail ? (
                  <LeaveRequestPreview {...buildLeaveRequestProps(resolutionDetail)} />
                ) : (
                  <div className={styles.detailCard}>불러오는 중...</div>
                )
              ) : selected.document_type === "income_resolution" ? (
                resolutionDetail ? (
                  <IncomeResolutionPreview {...buildIncomeResolutionProps(resolutionDetail)} />
                ) : (
                  <div className={styles.detailCard}>불러오는 중...</div>
                )
              ) : (
                <div className={styles.detailCard}>
                  <div className={styles.detailHeader}>
                    <span className={styles.draftNo}>{selected.draft_no}</span>
                    <span
                      className={styles.statusBadge}
                      style={{ color: STATUS_COLOR[selected.status], fontSize: 13 }}
                    >
                      {STATUS_LABEL[selected.status]}
                    </span>
                  </div>
                  <div className={styles.detailTitle}>{selected.title}</div>

                  <table className={styles.infoTable}>
                    <tbody>
                      <tr>
                        <th>문서종류</th>
                        <td>{DOC_TYPE_LABEL[selected.document_type]}</td>
                        <th>사업구분</th>
                        <td>{selected.category}</td>
                      </tr>
                      <tr>
                        <th>세부구분</th>
                        <td>{selected.subcategory}</td>
                        <th>세부옵션</th>
                        <td>{selected.detail_option || "-"}</td>
                      </tr>
                      <tr>
                        <th>거래처</th>
                        <td>{selected.partner_name}</td>
                        <th>금액</th>
                        <td style={{ fontWeight: 700 }}>
                          {Number(selected.amount).toLocaleString()}원
                        </td>
                      </tr>
                      <tr>
                        <th>작성일</th>
                        <td>{selected.draft_date}</td>
                        <th>청구일</th>
                        <td>{selected.claim_date || "-"}</td>
                      </tr>
                      <tr>
                        <th>귀속월</th>
                        <td>{selected.work_month || "-"}</td>
                        <th>거래방법</th>
                        <td>
                          {{ transfer: "계좌이체", card: "카드결제", cash: "현금결제" }[selected.pay_method] || "-"}
                        </td>
                      </tr>
                      {selected.desc && (
                        <tr>
                          <th>비고</th>
                          <td colSpan={3}>{selected.desc}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* 붙임자료 */}
                  {selected.attachment_items?.length > 0 && (
                    <div className={styles.attachSection}>
                      <div className={styles.attachTitle}>📎 붙임자료</div>
                      <ul className={styles.attachList}>
                        {selected.attachment_items.map((att, i) => (
                          <li key={i}>
                            {i + 1}. {att.name} — {att.count}부
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* 결재선 현황 */}
              <div className={styles.approvalLineCard}>
                <div className={styles.sectionTitle}>결재선 현황</div>
                <div className={styles.approvalLineFlow}>
                  {(selected.all_approval_lines || []).map((line, i) => (
                    <React.Fragment key={line.id}>
                      <div
                        className={styles.approvalStep}
                        style={{
                          borderColor:
                            line.result === "approved"
                              ? "#27ae60"
                              : line.result === "rejected"
                              ? "#e74c3c"
                              : line.result === "skipped"
                              ? "#9aa5ad"
                              : "#d0d7de",
                        }}
                      >
                        <div className={styles.approvalType}>{line.approval_type}</div>
                        <div className={styles.approvalName}>{line.approver__name}</div>
                        <div className={styles.approvalPosition}>{line.position}</div>
                        <div
                          className={styles.approvalResult}
                          style={{ color: RESULT_COLOR[line.result] }}
                        >
                          {RESULT_LABEL[line.result]}
                        </div>
                        {line.approved_at && (
                          <div className={styles.approvalDate}>
                            {line.approved_at.slice(0, 10)}
                          </div>
                        )}
                        {line.comment && (
                          <div className={styles.approvalComment}>💬 {line.comment}</div>
                        )}
                      </div>
                      {i < (selected.all_approval_lines?.length || 0) - 1 && (
                        <div className={styles.approvalArrow}>→</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* 승인/반려 영역 — inbox 탭에서만, 내 차례인 경우만 표시 */}
              {tab === "inbox" && selected.approval_line_id && (
                <div className={styles.actionCard}>
                  <div className={styles.sectionTitle}>결재 처리</div>

                  <div className={styles.commentWrap}>
                    <label>결재 의견 (선택)</label>
                    <textarea
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="승인 의견을 입력하거나 비워도 됩니다."
                      className={styles.commentArea}
                    />
                  </div>

                  <div className={styles.actionBtns}>
                    <button
                      className={styles.approveBtn}
                      onClick={handleApprove}
                      disabled={processing}
                    >
                      ✅ 승인
                    </button>
                    <button
                      className={styles.rejectBtn}
                      onClick={() => setShowRejectModal(true)}
                      disabled={processing}
                    >
                      ❌ 반려
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 반려 사유 입력 모달 */}
      {showRejectModal && (
        <div className={styles.modalOverlay} onClick={() => setShowRejectModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h4>반려 사유 입력</h4>
            <textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="반려 사유를 입력해주세요. (필수)"
              className={styles.commentArea}
              autoFocus
            />
            <div className={styles.modalBtns}>
              <button
                className={styles.rejectBtn}
                onClick={handleReject}
                disabled={processing || !comment.trim()}
              >
                반려 처리
              </button>
              <button
                className={styles.cancelBtn}
                onClick={() => setShowRejectModal(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}